import { useState } from "react";
import Link from "next/link";
import NavDropdown from "./NavDropdown";
import clsx from "clsx";
import { CaretDownIcon } from "../Icons/Icons";

interface NavigationItem {
  id: number;
  label: string;
  href?: string;
  target?: string;
}

export interface BreakpointConfig {
  md?: number;
  lg?: number;
  xl?: number;
}

interface SubNavigationProps {
  title: string;
  items: NavigationItem[];
  activeItem: number;
  onItemClick: (id: number) => void;
  itemsPerBreakpoint?: BreakpointConfig;
}

const SubNavigation = ({
  title,
  items,
  activeItem,
  onItemClick,
  itemsPerBreakpoint = { md: 2, lg: 3, xl: 5 },
}: SubNavigationProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleItemClick = (item: NavigationItem) => {
    if (!item.href) {
      onItemClick(item.id);
    }
  };

  const handleMobileItemClick = (item: NavigationItem) => {
    handleItemClick(item);
    setIsMobileMenuOpen(false);
  };

  const getItemVisibility = (index: number) => {
    const mdCount = itemsPerBreakpoint.md ?? 2;
    const lgCount = itemsPerBreakpoint.lg ?? 3;
    const xlCount = itemsPerBreakpoint.xl ?? 5;

    if (index < mdCount) return "flex";

    if (index < lgCount) return "hidden lg:flex";

    if (index < xlCount) return "hidden xl:flex";

    return "hidden";
  };

  return (
    <>
      <nav className="fixed top-[88px] max-w-[calc(100%-48px)] md:max-w-[calc(100%-64px)] lg:max-w-none z-50 min-w-[200px] w-full left-1/2 -translate-x-1/2 block md:hidden bg-white shadow-[1px_1px_20px_rgba(0,0,0,0.1)] px-8 border border-[#CCCCD1] rounded-2xl">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={clsx(
            "flex w-full items-center justify-start gap-[0.8rem] h-[60px]",
            isMobileMenuOpen && "border-b border-[#CCCCD1]",
          )}
          aria-expanded={isMobileMenuOpen}
          aria-haspopup="true"
        >
          <span className="text-normal font-medium">{title}</span>
          <CaretDownIcon
            className={`transition-transform duration-200 ${isMobileMenuOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isMobileMenuOpen && (
          <div className="w-full bg-white my-[2px] rounded-b-lg  leading-6">
            <ul className="list-none m-0 max-h-[400px] overflow-y-auto mt-4 mb-4">
              {items.map((item) => (
                <li key={item.id}>
                  {item.href ? (
                    <Link
                      href={item.href}
                      target={item.target}
                      rel={
                        item.target === "_blank"
                          ? "noopener noreferrer"
                          : undefined
                      }
                      onClick={() => handleMobileItemClick(item)}
                      className={`flex items-center px-4 py-3 cursor-pointer rounded-lg hover:bg-[#DEDEE3]  transition-colors relative
                        ${activeItem === item.id ? "bg-[#232B37] after:absolute after:rounded-l-lg" : ""}`}
                    >
                      <span
                        className={`text-base ${activeItem === item.id ? "font-medium text-white" : ""}`}
                      >
                        {item.label}
                      </span>
                    </Link>
                  ) : (
                    <div
                      onClick={() => handleMobileItemClick(item)}
                      className={`flex items-center px-4 py-3 cursor-pointer rounded-lg hover:bg-[#DEDEE3] transition-colors relative
                        ${activeItem === item.id ? "bg-[#232B37] after:absolute after:rounded-l-lg" : ""}`}
                    >
                      <span
                        className={`text-sm ${activeItem === item.id ? "font-medium text-white" : ""}`}
                      >
                        {item.label}
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      <nav className="fixed  top-4 left-1/2 -translate-x-1/2 translate-y-[125px] w-full md:w-[calc(100%-40px)] pointer-events-auto z-[5000] hidden md:flex max-w-[1320px] items-center rounded-[1.6rem] bg-white shadow-[0_4px_12px_0_rgba(0,0,0,0.10)] xl:overflow-hidden">
        <ul className="flex h-16 items-center gap-[16px] list-none m-0 py-4 px-10">
          <div className="flex gap-[6.4rem]">
            <li className="min-h-[44px] py-2 items-center justify-center cursor-default relative rounded-lg whitespace-nowrap flex hover:after:bg-transparent">
              <span className="text-base font-medium leading-6">{title}</span>
            </li>
            <div className="flex gap-4">
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className={clsx(
                    "relative",
                    getItemVisibility(index),
                    "after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:transition-colors",
                    activeItem === item.id
                      ? "after:bg-black"
                      : "after:bg-transparent",
                  )}
                >
                  {item.href ? (
                    <Link
                      href={item.href}
                      target={item.target}
                      rel={
                        item.target === "_blank"
                          ? "noopener noreferrer"
                          : undefined
                      }
                      onClick={() => handleItemClick(item)}
                      className="flex min-h-[44px] py-2 p-4 items-center justify-center cursor-pointer rounded-lg whitespace-nowrap hover:bg-gray-200"
                    >
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  ) : (
                    <div
                      onClick={() => handleItemClick(item)}
                      className="flex min-h-[44px] py-2 p-4 items-center justify-center cursor-pointer rounded-lg whitespace-nowrap hover:bg-gray-200"
                    >
                      <span className="text-sm">{item.label}</span>
                    </div>
                  )}
                </li>
              ))}

              {items.length > (itemsPerBreakpoint.md ?? 2) && (
                <li className="lg:hidden">
                  <NavDropdown
                    label={"More"}
                    items={items.slice(itemsPerBreakpoint.md ?? 2)}
                  />
                </li>
              )}

              {items.length > (itemsPerBreakpoint.lg ?? 3) && (
                <li className="hidden lg:flex xl:hidden">
                  <NavDropdown
                    label={"More"}
                    items={items.slice(itemsPerBreakpoint.lg ?? 3)}
                  />
                </li>
              )}

              {items.length > (itemsPerBreakpoint.xl ?? 5) && (
                <li className="hidden xl:flex">
                  <NavDropdown
                    label={"More"}
                    items={items.slice(itemsPerBreakpoint.xl ?? 5)}
                  />
                </li>
              )}
            </div>
          </div>
        </ul>
      </nav>
    </>
  );
};

export default SubNavigation;
